/**
 * @file App.js
 * @description Main application component that defines the routing structure and global providers
 * for the CSF (Carolina Sports Foundation) frontend application.
 *
 * This file serves as the central routing hub for the entire application, organizing routes
 * into three main categories:
 * 1. Public routes (login, register, forgot password)
 * 2. Protected parent/coach routes (dashboard, classes, calendar, etc.)
 * 3. Admin-only routes (admin dashboard, class management, financial reports, etc.)
 *
 * Key Features:
 * - Role-based route protection using ProtectedRoute component
 * - Stripe Elements provider wrapping checkout flow
 * - Toast notification configuration
 * - Nested routing for admin section with AdminLayout
 * - Auth routes using shared AuthLayout shell
 *
 * @requires react
 * @requires react-router-dom
 * @requires react-hot-toast
 * @requires @stripe/react-stripe-js
 */

// ========================================
// IMPORTS - Core Libraries
// ========================================
import React from "react";
import { Routes, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Elements } from "@stripe/react-stripe-js";

// ========================================
// IMPORTS - Configuration
// ========================================
import stripePromise from "./config/stripe.config";

// ========================================
// IMPORTS - Layouts
// ========================================
import AuthLayout from "./pages/Authlayout";
import AdminLayout from "./layouts/AdminLayout";

// ========================================
// IMPORTS - Authentication Pages
// ========================================
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import CompleteProfile from "./pages/CompleteProfile";

// ========================================
// IMPORTS - Parent Pages
// ========================================
import Dashboard from "./pages/Dashboard";
import Calender from "./pages/Calender";
import Gallery from "./pages/Gallery";
import Attendence from "./pages/Attendence";
import Settings from "./pages/Settings";
import ContactForm from "./pages/ContactForm";
import PaymentBilling from "./pages/PaymentBilling";
import Badges from "./pages/Badges";
import Classes from "./pages/Classes";
import ClassDetail from "./pages/AdminDashboard/ClassDetail";
import Waivers from "./pages/Waivers";
import ProgramOverview from "./pages/ProgramOverview";
import Events from "./pages/Events";
import Checkout from "./pages/CheckOut";
import ClassRegister from "./pages/ClassRegister";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import ClassList from "./pages/AdminDashboard/ClassList";
import Resgister from "./pages/AdminDashboard/Resgister";

// ========================================
// IMPORTS - Coach Pages
// ========================================
import DashboardCoach from "./pages/CoachDashboard/DashboardCoach";
import CheckIn from "./pages/CoachDashboard/CheckIn";
import JerseyHandout from "./pages/CoachDashboard/JerseyHandout";
import JerseyOutstanding from "./pages/AdminDashboard/JerseyOutstanding";
import JerseySizeRequests from "./pages/AdminDashboard/JerseySizeRequests";
import SentMessages from "./pages/AdminDashboard/SentMessages";
import ScheduledEmails from "./pages/AdminDashboard/ScheduledEmails";
import CoachGallery from "./pages/CoachDashboard/CoachGallery";

// ========================================
// IMPORTS - Admin Pages
// ========================================
import MissionControl from "./pages/AdminDashboard/MissionControl";
import Financials from "./pages/AdminDashboard/Financials";
import Clients from "./pages/AdminDashboard/Clients";
import RegisterChild from "./pages/AdminDashboard/RegisterChild";
import EditChild from "./pages/EditChild";
import MyChildren from "./pages/MyChildren";
import AdminClasses from "./pages/AdminDashboard/Classes";
import ClassRoster from "./pages/AdminDashboard/ClassRoster";
import AdminUsers from "./pages/AdminDashboard/Users";
import AdminPrograms from "./pages/AdminDashboard/Programs";
import AdminAreas from "./pages/AdminDashboard/Areas";
import AdminSchools from "./pages/AdminDashboard/Schools";
import Installments from "./pages/AdminDashboard/Installments";
import Enrollments from "./pages/AdminDashboard/Enrollments";
import ClassAttendance from "./pages/AdminDashboard/ClassAttendance";
import Waitlist from "./pages/AdminDashboard/Waitlist";
import Invoices from "./pages/AdminDashboard/Invoices";
import RefundsManagement from "./pages/AdminDashboard/RefundsManagement";
import CancellationRequests from "./pages/AdminDashboard/CancellationRequests";
import EventsManagement from "./pages/AdminDashboard/EventsManagement";
import BadgesManagement from "./pages/AdminDashboard/BadgesManagement";
import PhotosManagement from "./pages/AdminDashboard/PhotosManagement";
import AnnouncementsManagement from "./pages/AdminDashboard/AnnouncementsManagement";
import AdminCalendar from "./pages/AdminDashboard/AdminCalendar";
import DiscountManagement from "./pages/AdminDashboard/DiscountManagement";
import MassEmail from "./pages/AdminDashboard/MassEmail";
import MarketingContacts from "./pages/AdminDashboard/marketing/Contacts";
import MarketingCompose from "./pages/AdminDashboard/marketing/Compose";
import MarketingSegments from "./pages/AdminDashboard/marketing/Segments";
import MarketingImportHistory from "./pages/AdminDashboard/marketing/ImportHistory";
import MarketingSendHistory from "./pages/AdminDashboard/marketing/SendHistory";
import SendGridSync from "./pages/AdminDashboard/marketing/SendGridSync";
import WaiversManagement from "./pages/admin/WaiversManagement";
import WaiverReports from "./pages/admin/WaiverReports";
import PublicRoster from "./pages/PublicRoster";

// ========================================
// IMPORTS - Shared Components
// ========================================
import ProtectedRoute from "./components/ProtectedRoute";
import AddStudent from "./components/AddStudent";
import { ChildrenProvider } from "./context/ChildrenContext";

/**
 * App Component
 *
 * Main application component that defines the complete routing structure for the CSF application.
 * Handles three user roles (parent, coach, admin) with appropriate route protection and layouts.
 *
 * @component
 * @returns {JSX.Element} The root application component with all routes configured
 *
 * @example
 * // Used in index.js wrapped with providers
 * <BrowserRouter>
 *   <AuthProvider>
 *     <ApiProvider>
 *       <App />
 *     </ApiProvider>
 *   </AuthProvider>
 * </BrowserRouter>
 */
export default function App() {
  return (
    <>
      {/* ========================================
    TOAST NOTIFICATIONS CONFIGURATION
    ========================================
    React Hot Toast provider for app-wide notifications
    - Position: top-right corner
    - Auto-dismiss after 3-4 seconds
    - Custom styling matching brand colors
    - Different icons for success/error states
*/}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#F3BC48", // Brand gold color
              secondary: "#fff",
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: "#ef4444", // Red for errors
              secondary: "#fff",
            },
          },
        }}
      />

      {/* ========================================
    ROUTING CONFIGURATION
    ========================================
    Complete routing structure organized by access level:
    1. Public routes (auth)
    2. Protected routes (parent/coach)
    3. Admin-only routes
*/}
      <ChildrenProvider>
        <Routes>
          {/* ========================================
    PUBLIC ROUTES
    ======================================== */}

          {/* Public marketing/overview page - Currently disabled, uses / for registration */}
          {/* <Route path="/" element={<ProgramOverview />} /> */}

          {/*
  Auth routes share the AuthLayout shell
  AuthLayout provides consistent branding and layout for login/register/forgot-password pages
*/}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/force-password-change"
              element={
                <ProtectedRoute>
                  <ForcePasswordChange />
                </ProtectedRoute>
              }
            />

            {/* Every account needs a phone number so a coach can reach the
                family. Google sign-in never collected one, so this catches
                both new sign-ins and the accounts already missing it. */}
            <Route
              path="/complete-profile"
              element={
                <ProtectedRoute>
                  <CompleteProfile />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* ========================================
    PUBLIC CLASS BROWSING
    ========================================
    These routes allow public access to view classes before login
*/}
          <Route path="/" element={<Resgister />} />
          <Route path="/class-list" element={<ClassList />} />
          <Route path="/class-detail" element={<ClassDetail />} />
          <Route path="/public/roster/:shareToken" element={<PublicRoster />} />

          {/* ========================================
    PROTECTED PARENT/COACH ROUTES
    ========================================
    Routes accessible to authenticated parents and coaches
    ProtectedRoute checks for valid JWT token and redirects to /login if not authenticated
*/}

          {/* Program Overview - Landing page after login */}
          <Route
            path="/overview"
            element={
              <ProtectedRoute>
                <ProgramOverview />
              </ProtectedRoute>
            }
          />

          {/* Parent Dashboard - Main dashboard showing enrollments, events, attendance */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requiredRole="parent">
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Coach Dashboard - Shows assigned classes and check-in functionality */}
          <Route
            path="/coachdashboard"
            element={
              <ProtectedRoute>
                <DashboardCoach />
              </ProtectedRoute>
            }
          />

          {/* Calendar - View class schedules and events, RSVP to events */}
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <Calender />
              </ProtectedRoute>
            }
          />

          {/* Events - View and RSVP to events for enrolled classes */}
          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <Events />
              </ProtectedRoute>
            }
          />

          {/* Photo Gallery - View class photos uploaded by coaches */}
          <Route
            path="/photos"
            element={
              <ProtectedRoute>
                <Gallery />
              </ProtectedRoute>
            }
          />

          {/* Coach Photo Upload - Coaches can upload photos to class galleries */}
          <Route
            path="/Gallery"
            element={
              <ProtectedRoute>
                <CoachGallery />
              </ProtectedRoute>
            }
          />

          {/* Attendance - View child attendance history and streaks */}
          <Route
            path="/attendance"
            element={
              <ProtectedRoute>
                <Attendence />
              </ProtectedRoute>
            }
          />

          {/* Settings - Account settings, profile management */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />

          {/* Account Settings - Alias for settings */}
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />

          {/* Password Settings - Settings page with password section pre-selected */}
          <Route
            path="/settings/password"
            element={
              <ProtectedRoute>
                <Settings initialSection="password" />
              </ProtectedRoute>
            }
          />

          {/* Contact Form - Contact support */}
          <Route
            path="/contactus"
            element={
              <ProtectedRoute>
                <ContactForm />
              </ProtectedRoute>
            }
          />

          {/* Payment & Billing - View invoices, payment methods, payment history */}
          <Route
            path="/paymentbilling"
            element={
              <ProtectedRoute requiredRole="parent">
                <PaymentBilling initialSection="payment" />
              </ProtectedRoute>
            }
          />

          {/* Badges - View earned badges and achievement progress */}
          <Route
            path="/badges"
            element={
              <ProtectedRoute requiredRole="parent">
                <Badges />
              </ProtectedRoute>
            }
          />

          {/* Coach Check-In - Quick student attendance check-in for coaches */}
          <Route
            path="/checkin"
            element={
              <ProtectedRoute>
                <CheckIn />
              </ProtectedRoute>
            }
          />

          {/* Jersey Handout - Coaches record which players have their jersey.
              Unlike check-in there is no record per session: the status is
              permanent and attached to the registration. */}
          <Route
            path="/jersey-handout"
            element={
              <ProtectedRoute>
                <JerseyHandout />
              </ProtectedRoute>
            }
          />

          {/* Class Browsing - Browse and filter available classes */}
          <Route
            path="/class"
            element={
              <ProtectedRoute>
                <Classes />
              </ProtectedRoute>
            }
          />

          {/* Class Detail - Public class overview by slug or id. This is the
              link shared with families: they see the class before signing up. */}
          <Route path="/class/:classRef" element={<ClassDetail />} />

          {/* Custom URL Registration - Short URL for class registration */}
          <Route path="/register/:slug" element={<ClassRegister />} />

          {/* Add Child - Add new child profile */}
          <Route
            path="/addchild"
            element={
              <ProtectedRoute requiredRole="parent">
                <AddStudent />
              </ProtectedRoute>
            }
          />

          {/* Waivers - Sign required waivers, view waiver history */}
          <Route
            path="/waivers"
            element={
              <ProtectedRoute requiredRole="parent">
                <Waivers />
              </ProtectedRoute>
            }
          />

          {/*
  Checkout - Enrollment checkout with Stripe payment
  Wrapped in Stripe Elements provider for payment form integration
*/}
          <Route
            path="/checkout"
            element={
              <ProtectedRoute requiredRole="parent">
                <Elements stripe={stripePromise}>
                  <Checkout />
                </Elements>
              </ProtectedRoute>
            }
          />

          {/* Payment Result Pages - Show success/cancel messages after Stripe checkout */}
          <Route
            path="/payment/success"
            element={
              <ProtectedRoute requiredRole="parent">
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment/cancel"
            element={
              <ProtectedRoute requiredRole="parent">
                <PaymentCancel />
              </ProtectedRoute>
            }
          />

          {/* Register Child - Register new child (parent only) */}
          <Route
            path="/registerchild"
            element={
              <ProtectedRoute requiredRole="parent">
                <RegisterChild />
              </ProtectedRoute>
            }
          />

          {/* Edit Child - Edit existing child (parent only) */}
          <Route
            path="/editchild/:childId"
            element={
              <ProtectedRoute requiredRole="parent">
                <EditChild />
              </ProtectedRoute>
            }
          />

          {/* My Children - Manage children profiles (parent only) */}
          <Route
            path="/mychildren"
            element={
              <ProtectedRoute requiredRole="parent">
                <MyChildren />
              </ProtectedRoute>
            }
          />

          {/* ========================================
    ADMIN-ONLY ROUTES
    ========================================
    Routes accessible only to users with admin role
    Uses AdminLayout wrapper for consistent admin navigation and layout
    ProtectedRoute with requiredRole="admin" enforces role-based access control
*/}

          {/* Class Roster - Standalone page accessible via shareable link */}
          <Route
            path="/roster/:classId"
            element={
              <ProtectedRoute requiredRole="admin">
                <ClassRoster />
              </ProtectedRoute>
            }
          />

          {/*
  Admin Layout Wrapper - Nested routes share AdminLayout
  AdminLayout provides admin sidebar navigation and header
*/}
          <Route
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            {/* Admin Dashboard - Overview of metrics, revenue, enrollments */}
            <Route path="/admin" element={<MissionControl />} />

            {/* Class Management - Create, edit, delete classes and programs */}
            <Route path="/admin/classes" element={<AdminClasses />} />

            {/* User Management - Create, edit, delete users */}
            <Route path="/admin/users" element={<AdminUsers />} />

            {/* Program Management - Create, edit, delete programs */}
            <Route path="/admin/programs" element={<AdminPrograms />} />

            {/* Area Management - Create, edit, delete areas */}
            <Route path="/admin/areas" element={<AdminAreas />} />

            {/* School Management - Create, edit, delete schools */}
            <Route path="/admin/schools" element={<AdminSchools />} />

            {/* Waiver Management - Create and edit waiver forms */}
            <Route path="/admin/waivers" element={<WaiversManagement />} />

            {/* Outstanding Jerseys - who still needs one, and where to post it */}
            <Route path="/admin/jerseys" element={<JerseyOutstanding />} />

            {/* Sent Messages - staff emails plus the notices schools receive */}
            <Route path="/admin/sent-messages" element={<SentMessages />} />

            {/* Scheduled Emails - mass emails waiting to go out, once or on repeat */}
            <Route path="/admin/scheduled-emails" element={<ScheduledEmails />} />

            {/* New Size Requests - replacement jerseys the office is posting */}
            <Route
              path="/admin/jersey-size-requests"
              element={<JerseySizeRequests />}
            />

            {/* Waiver Reports - View waiver completion reports */}
            <Route path="/admin/waiver-reports" element={<WaiverReports />} />

            {/* Client Management - Manage parent and child accounts */}
            <Route path="/clients" element={<Clients />} />

            {/* Financial Reports - Revenue reports by class, program, date range */}
            <Route path="/financials" element={<ProtectedRoute requiredRole="owner"><Financials /></ProtectedRoute>} />

            {/* Installment Management - View and manage payment plans */}
            <Route path="/admin/installments" element={<ProtectedRoute requiredRole="owner"><Installments /></ProtectedRoute>} />

            {/* Enrollment Management - View, transfer, cancel enrollments */}
            <Route path="/admin/enrollments" element={<Enrollments />} />

            {/* Class Attendance - owner/admin view of check-ins taken by coaches */}
            <Route path="/admin/attendance" element={<ClassAttendance />} />

            {/* Waitlist Management - Manage class waitlists */}
            <Route path="/admin/waitlist" element={<Waitlist />} />

            {/* Invoice Management - View and manage invoices */}
            <Route path="/admin/invoices" element={<ProtectedRoute requiredRole="owner"><Invoices /></ProtectedRoute>} />

            {/* Refunds Management - Review and process refund requests */}
            <Route path="/admin/refunds" element={<ProtectedRoute requiredRole="owner"><RefundsManagement /></ProtectedRoute>} />

            {/* Cancellation Requests - Review enrollment cancellation requests */}
            <Route
              path="/admin/cancellations"
              element={<CancellationRequests />}
            />

            {/* Events Management - Create and manage events */}
            <Route path="/admin/events" element={<EventsManagement />} />

            {/* Badges Management - Create and award badges */}
            <Route path="/admin/badges" element={<BadgesManagement />} />

            {/* Photos Management - Upload and manage photos */}
            <Route path="/admin/photos" element={<PhotosManagement />} />

            {/* Announcements Management - Create and manage announcements with multi-class support */}
            <Route
              path="/admin/announcements"
              element={<AnnouncementsManagement />}
            />

            {/* Calendar - View events across all classes */}
            <Route path="/admin/calendar" element={<AdminCalendar />} />

            {/* Discount Management - Create and manage discount codes */}
            <Route path="/admin/discounts" element={<DiscountManagement />} />

            {/* Mass Email - Send bulk emails to parents */}
            <Route path="/admin/mass-email" element={<MassEmail />} />

            {/* Marketing module - contacts, segments, compose, history */}
            <Route
              path="/admin/marketing/contacts"
              element={<MarketingContacts />}
            />
            <Route
              path="/admin/marketing/compose"
              element={<MarketingCompose />}
            />
            <Route
              path="/admin/marketing/segments"
              element={<MarketingSegments />}
            />
            <Route
              path="/admin/marketing/imports"
              element={<MarketingImportHistory />}
            />
            <Route
              path="/admin/marketing/sends"
              element={<MarketingSendHistory />}
            />
            <Route
              path="/admin/marketing/sendgrid"
              element={<SendGridSync />}
            />
          </Route>
        </Routes>
      </ChildrenProvider>
    </>
  );
}
